import { useCallback, useMemo, useState } from "react";
import { Box, useDisclosure } from "@chakra-ui/react";
import CDS from "design-system";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { Task } from "features/Resources/Task/models";
import { createV2TaskColumns } from "features/Resources/Task/Components/Table/V2TasksTable/columns";
import SendReminderModal from "features/SendMessage/Components/SendReminderModal";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";

interface SortOption {
  label: string;
  value: string;
}

interface V2TasksTableClientSideProps {
  data: Task[];
  isLoading?: boolean;
  onExpand: (item: Task) => void;
  sortOptions?: SortOption[];
  currentSort?: string;
  defaultSortString?: string;
  onSortChange?: (value: string) => void;
  showTaskActionButtons?: boolean;
  showJourney?: boolean;
}

export default function V2TasksTableClientSide({
  data,
  isLoading = false,
  onExpand,
  sortOptions,
  currentSort,
  defaultSortString,
  onSortChange,
  showTaskActionButtons = false,
  showJourney = false,
}: V2TasksTableClientSideProps) {
  const reminderDisclosure = useDisclosure();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const handleOpenRemind = useCallback((task: Task) => {
    setSelectedTask(task);
    reminderDisclosure.onOpen();
  }, [reminderDisclosure]);

  const columns = useMemo(() => createV2TaskColumns(onExpand, handleOpenRemind, showTaskActionButtons, showJourney), [handleOpenRemind, onExpand, showJourney, showTaskActionButtons]);

  return (
    <>
      <ControlledDataTable<Task>
        data={data || []}
        isLoading={isLoading}
        columns={columns}
        emptyStateComponent={(
          <Box textAlign="center" p={4}>
            <CDS.Text16 color="chakra-subtle-text">
              No tasks found
            </CDS.Text16>
          </Box>
        )}
        sx={{ backgroundColor: "transparent", width: "100%" }}
        containerSx={{ width: "100%" }}
        sortOptions={sortOptions}
        currentSort={currentSort}
        defaultSortString={defaultSortString}
        onSortChange={onSortChange}
      />
      {selectedTask && (
        <SendReminderModal
          isOpen={reminderDisclosure.isOpen}
          onClose={reminderDisclosure.onClose}
          tasks={[selectedTask]}
          preselectedUser={selectedTask.assignee}
        />
      )}
      <TaskActionModalByQueryParam />
    </>
  );
}
